/**
 * Top-left phase countdown widget for Survey Says — copied from WhatIs
 * ui/countdown.ts. Big Oswald numerals + a phase label that ticks down to 0.
 *
 * Survey Says uses two phases:
 *   - ss_question_open  -> setPhase('Answer', endsAtMs)
 *   - reveal sequence   -> hide() (no countdown during reveals)
 */

const STYLES = `
  @keyframes ss-countdown-in {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes ss-countdown-pulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.08); }
  }
  .ss-countdown {
    position: absolute;
    top: 40px;
    left: 40px;
    z-index: 60;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    pointer-events: none;
    padding: 10px 22px 8px 22px;
    border-radius: 14px;
    background: linear-gradient(180deg, rgba(6,12,233,0.62) 0%, rgba(3,4,40,0.82) 100%);
    border: 2px solid var(--sg-primary, #FFC43D);
    box-shadow: 0 4px 30px rgba(0,0,0,0.4);
    animation: ss-countdown-in 260ms ease-out;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    color: #fff;
    min-width: 160px;
  }
  .ss-countdown.hidden { display: none; }
  .ss-countdown-value {
    font-size: 92px;
    font-weight: 700;
    line-height: 0.95;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 18px rgba(255,196,61,0.55), 0 3px 6px rgba(0,0,0,0.6);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.01em;
  }
  .ss-countdown-label {
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.28em;
    color: var(--sg-accent, #E91E63);
    text-transform: uppercase;
    text-shadow: 0 2px 4px rgba(0,0,0,0.6);
  }
  .ss-countdown.ss-warn .ss-countdown-value {
    animation: ss-countdown-pulse 600ms ease-in-out infinite;
  }
  .ss-countdown.ss-danger {
    border-color: #F87171;
  }
  .ss-countdown.ss-danger .ss-countdown-value {
    color: #F87171;
    text-shadow: 0 0 20px rgba(248,113,113,0.7), 0 3px 6px rgba(0,0,0,0.6);
    animation: ss-countdown-pulse 420ms ease-in-out infinite;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('ss-countdown-style')) return;
  const s = document.createElement('style');
  s.id = 'ss-countdown-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export interface CountdownHandle {
  el: HTMLDivElement;
  /**
   * Arm the countdown for a phase. Both timestamps are wall-clock ms.
   * `startsAtMs` (optional): when provided AND in the future, the widget
   * freezes at the full phase duration (= endsAtMs - startsAtMs) until that
   * timestamp passes, then ticks down. Implements broadcast-delay-aware
   * countdown so viewers see the FULL answer window on screen.
   */
  setPhase: (label: string, endsAtMs: number, startsAtMs?: number) => void;
  hide: () => void;
  dispose: () => void;
}

export function createCountdown(mount: HTMLElement): CountdownHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'ss-countdown hidden';
  el.innerHTML = `
    <div class="ss-countdown-value" data-role="value">0</div>
    <div class="ss-countdown-label" data-role="label"></div>
  `;
  mount.appendChild(el);
  const valueEl = el.querySelector<HTMLDivElement>('[data-role="value"]')!;
  const labelEl = el.querySelector<HTMLDivElement>('[data-role="label"]')!;

  let endsAt = 0;
  let startsAt = 0;
  let currentLabel = '';
  let raf: number | null = null;

  function renderTick(): void {
    const now = Date.now();
    if (startsAt > 0 && now < startsAt) {
      const fullMs = Math.max(0, endsAt - startsAt);
      const str = String(Math.ceil(fullMs / 1000));
      if (valueEl.textContent !== str) valueEl.textContent = str;
      el.classList.remove('ss-warn', 'ss-danger');
      raf = requestAnimationFrame(renderTick);
      return;
    }
    const remainingMs = Math.max(0, endsAt - now);
    const seconds = Math.ceil(remainingMs / 1000);
    const str = String(seconds);
    if (valueEl.textContent !== str) valueEl.textContent = str;
    const remSec = remainingMs / 1000;
    el.classList.toggle('ss-warn', remSec <= 5 && remSec > 3);
    el.classList.toggle('ss-danger', remSec <= 3);
    if (remainingMs <= 0) {
      valueEl.textContent = '0';
      raf = null;
      return;
    }
    raf = requestAnimationFrame(renderTick);
  }

  function setPhase(label: string, endsAtMs: number, startsAtMs?: number): void {
    const normalizedLabel = label.toUpperCase();
    const now = Date.now();
    const skew = endsAtMs - now;
    const resolvedEnd = (skew > 120_000 || skew < -10_000) ? now + 30_000 : endsAtMs;
    const nextStartsAt = startsAtMs && startsAtMs > now ? startsAtMs : 0;
    if (
      normalizedLabel === currentLabel &&
      Math.abs(resolvedEnd - endsAt) < 250 &&
      Math.abs(nextStartsAt - startsAt) < 250
    ) {
      if (el.classList.contains('hidden')) el.classList.remove('hidden');
      if (raf === null) renderTick();
      return;
    }
    currentLabel = normalizedLabel;
    endsAt = resolvedEnd;
    startsAt = nextStartsAt;
    labelEl.textContent = normalizedLabel;
    el.classList.remove('hidden');
    el.classList.remove('ss-warn', 'ss-danger');
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
    if (raf !== null) cancelAnimationFrame(raf);
    renderTick();
  }

  function hide(): void {
    el.classList.add('hidden');
    currentLabel = '';
    if (raf !== null) {
      cancelAnimationFrame(raf);
      raf = null;
    }
  }

  return {
    el,
    setPhase,
    hide,
    dispose() {
      hide();
      el.remove();
    },
  };
}
